import type { PriceStrategy } from "@/lib/channels";
import { pricingConfigFromProviderConfig } from "@/lib/listing-pricing";
import { resolveVariantEffectiveChannelPrice } from "@/lib/listing-channel-price-display";

const STRATEGIES: PriceStrategy[] = [
  "inherit",
  "fixed_override",
  "fixed_markup",
  "percentage_markup",
];

function normalizeStrategy(value: string | null | undefined): PriceStrategy {
  return STRATEGIES.includes(value as PriceStrategy) ? (value as PriceStrategy) : "inherit";
}

export type ListingTemplatePreviewPriceVariant = {
  channel_price?: number | null;
  effective_channel_price?: number | null;
  selling_price?: number | null;
  variant?: {
    selling_price?: number | null;
  } | null;
  variants?: {
    selling_price?: number | null;
  } | null;
  [key: string]: unknown;
};

/**
 * Preview pricing must use the same intended-provider-price authority shown in
 * the listing workspace: listing strategy + provider_config + per-variant
 * channel override. Stored effective_channel_price can be stale or absent on a
 * draft and must not make preview fall back to Product Master pricing.
 */
export function listingTemplatePreviewVariantsWithIntendedPrice<T extends ListingTemplatePreviewPriceVariant>(opts: {
  priceStrategy?: string | null;
  providerConfig?: Record<string, unknown> | null;
  productSellingPrice?: number | null;
  variants: T[];
}): T[] {
  const priceStrategy = normalizeStrategy(opts.priceStrategy);
  const pricingConfig = pricingConfigFromProviderConfig(opts.providerConfig);

  return opts.variants.map((variant) => {
    const nested = variant.variant ?? variant.variants ?? null;
    const variantSellingPrice =
      nested?.selling_price ?? variant.selling_price ?? null;

    const effective = resolveVariantEffectiveChannelPrice({
      priceStrategy,
      pricingConfig,
      productSellingPrice: opts.productSellingPrice ?? null,
      variantSellingPrice,
      channelPrice: variant.channel_price ?? null,
    }).effective;

    return {
      ...variant,
      effective_channel_price: effective,
    };
  });
}
